import React, { useState, useEffect } from "react";
import { type ScreeningMatchType } from "~/graphql/supergraphTypes";
import { useEnableAiScreening } from "~/pages/Compliance/flags";
import { FlattenedProfileType, ScoringEntry, ScreeningRecord } from "../../types";
import { AiSummary } from "../AiSummaryCard";

type ScoringMap = Map<number, ScoringEntry>;

interface AiScreeningProviderProps {
  currentSelectedProfile?: FlattenedProfileType & {
    screeningRecord?: ScreeningRecord | null;
  };
  riskAssessmentFailed?: boolean;
  children: (aiData: {
    aiSummary: AiSummary | null;
    isAiLoading: boolean;
    aiProcessed: boolean;
    aiScoringResults: ScoringMap;
    riskAssessmentFailed: boolean;
  }) => React.ReactNode;
}

export const AiScreeningProvider: React.FC<AiScreeningProviderProps> = ({
  currentSelectedProfile,
  riskAssessmentFailed = false,
  children,
}) => {
  const enableAiScreening = useEnableAiScreening();
  const [aiSummary, setAiSummary] = useState<AiSummary | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiProcessed, setAiProcessed] = useState(false);
  const [aiScoringResults, setAiScoringResults] = useState<ScoringMap>(new Map());

  // Extract risk assessment data from existing screening matches
  useEffect(() => {
    if (!enableAiScreening || !currentSelectedProfile?.screeningRecord?.matches) {
      return;
    }

    const scoringMap: ScoringMap = new Map();
    let highRiskCount = 0;
    let mediumRiskCount = 0;
    let lowRiskCount = 0;

    currentSelectedProfile.screeningRecord.matches.forEach((match: ScreeningMatchType) => {
      if (match.riskAssessment) {
        scoringMap.set(match.id, {
          ai_score: match.riskAssessment.score || 0,
          explanation: match.riskAssessment.scoreExplanation || "",
          recommended_action: match.riskAssessment.recommendedAction || "",
        });

        // Categorize risk levels based on score (0-100 scale)
        const score = match.riskAssessment.score;
        if (score >= 80) {
          highRiskCount++;
        } else if (score >= 40) {
          mediumRiskCount++;
        } else {
          lowRiskCount++;
        }
      }
    });

    setAiScoringResults(scoringMap);
    setAiSummary({
      totalMatches: currentSelectedProfile.screeningRecord.matches.length,
      highRiskCount,
      mediumRiskCount,
      lowRiskCount,
    });
    setAiProcessed(true);
    setIsAiLoading(false);
  }, [currentSelectedProfile?.screeningRecord?.matches, enableAiScreening]);

  return (
    <>
      {children({
        aiSummary,
        isAiLoading,
        aiProcessed,
        aiScoringResults,
        riskAssessmentFailed,
      })}
    </>
  );
};
